import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { desaturate } from 'polished';

import Sizes from '../definitions/Sizes';
import Colours from '../definitions/Colours';
import Typography from '../definitions/Typography';
import MessagePropType from '../definitions/MessagePropType';
import Paragraph from './Paragraph';
import StarRating from './StarRating';


function StarReviews({
    title,
    titleURL,
    titleTarget = '_blank',
    onTitleClick,

    ratingPercent,
    reviewCount,
    reviewURL,
    reviewTarget = '_blank',
    onReviewsClick,

    size = SIZES.MEDIUM,
    alignment = ALIGNMENTS.LEFT,

    forceStars = false,
    forceReviewText = false,
    reviewFontWeight = Typography.WEIGHTS.MEDIUM,

    ...otherProps
}) {
    if (reviewTarget === 'newTab') {  // synonym for compatibility with withLink()
        reviewTarget = '_blank';
    }
    if (titleTarget === 'newTab') {  // synonym for compatibility with withLink()
        titleTarget = '_blank';
    }

    const titleLinkProps = titleURL || onTitleClick
        ? {
            forwardedAs: 'a',
            href: titleURL,
            target: titleTarget,
            rel: titleTarget === '_blank' ? 'noopener' : '',
            onClick: onTitleClick,
        }
        : undefined;

    // by default, no reviews means _do not show_ stars; forceStars overrides that
    const haveStarsToShow = reviewCount > 0 || forceStars;
    // by default, no reviews means _do not show_ "No reviews yet"; must opt in with forceReviewText to display it
    const haveReviewTextToShow = reviewCount > 0 || forceReviewText;

    const isTitleRenderedInParagraph = React.isValidElement(title)
        ? React.Children.only(title)?.type === 'FormattedMessage'
        : true;

    return (
        <Container
            alignment={alignment}
            titleMargin={titleMargin[size]}
            titleFontSize={titleFontSize[size]}
            reviewTextSize={reviewTextSize[size]}
            size={size}
            {...otherProps}
        >
            <div className="title-wrapper">
                <Choose>
                    <When condition={isTitleRenderedInParagraph}>
                        <Paragraph
                            className="title rh-m-0"
                            weight="medium"
                            message={title}
                            {...titleLinkProps}
                        />
                    </When>
                    <Otherwise>
                        {title}
                    </Otherwise>
                </Choose>
            </div>

            <If condition={haveStarsToShow || haveReviewTextToShow}>
                <div className="stars-and-review-text rh-display-inline-flex rh-flex-wrap rh-align-items-center">
                    <If condition={haveStarsToShow}>
                        <StarRating
                            className="rh-mr-0_75"
                            starSize={starSize[size]}
                            ratingPercent={ratingPercent}
                        />
                    </If>

                    <If condition={haveReviewTextToShow}>
                        <span className={'rh-title-l rh-m-0 rh-fg-blackberry weight-regular review-text'}>
                            <Choose>
                                <When condition={reviewCount > 0}>
                                    <ReviewCount
                                        href={reviewURL}
                                        target={reviewURL && reviewTarget}
                                        rel={reviewURL && reviewTarget === '_blank' ? 'noopener' : ''}
                                        onClick={onReviewsClick}
                                        as={reviewURL || onReviewsClick ? 'a' : undefined}
                                        reviewFontWeight={reviewFontWeight}
                                    >
                                        <FormattedMessage
                                            id="base-ui.star-reviews-some"
                                            defaultMessage={`{reviewCount, plural,
                                                one {{reviewCount, number} review}
                                                other {{reviewCount, number} reviews}
                                            }`}
                                            values={{ reviewCount }}
                                        />
                                    </ReviewCount>
                                </When>

                                <When condition={forceReviewText}>
                                    <span>
                                        <FormattedMessage
                                            id="base-ui.star-reviews-none-yet"
                                            defaultMessage="No reviews yet"
                                        />
                                    </span>
                                </When>
                            </Choose>
                        </span>
                    </If>
                </div>
            </If>
        </Container>
    );
}


const SIZES = {
    SMALL: 'small',
    MEDIUM: 'medium',
    LARGE: 'large',
    HUGE: 'huge',
};

const ALIGNMENTS = {
    LEFT: 'left',
    CENTER: 'center',
};

StarReviews.propTypes = {
    title: MessagePropType.isRequired,
    titleURL: PropTypes.string,
    titleTarget: PropTypes.string,
    onTitleClick: PropTypes.func,

    ratingPercent: PropTypes.number.isRequired,
    reviewCount: PropTypes.number,
    reviewURL: PropTypes.string,
    reviewTarget: PropTypes.string,
    onReviewsClick: PropTypes.func,

    size: PropTypes.oneOf(Object.values(SIZES)),
    alignment: PropTypes.oneOf(Object.values(ALIGNMENTS)),

    forceStars: PropTypes.bool,
    forceReviewText: PropTypes.bool,
    reviewFontWeight: PropTypes.oneOf(Object.values(Typography.WEIGHTS)),
};


const titleFontSize = {
    [SIZES.SMALL]: Sizes.FONTS.S,
    [SIZES.MEDIUM]: Sizes.FONTS.L,
    [SIZES.LARGE]: Sizes.FONTS.XL,
    [SIZES.HUGE]: Sizes.FONTS['3XL'],
};

const titleMargin = {
    [SIZES.SMALL]: Sizes.SPACING.EIGHTH,
    [SIZES.MEDIUM]: Sizes.SPACING.QUARTER,
    [SIZES.LARGE]: Sizes.SPACING.QUARTER,
    [SIZES.HUGE]: Sizes.SPACING.QUARTER,
};

const starSize = {
    [SIZES.SMALL]: Sizes.ICONS['3XS'],      // 0.75
    [SIZES.MEDIUM]: Sizes.ICONS['2XS'],     // 1
    [SIZES.LARGE]: '1.25rem',               // No 1.25 multiplier in Sizes.ICONS
    [SIZES.HUGE]: Sizes.ICONS['XS'],        // 1.5
};

const reviewTextSize = {
    [SIZES.SMALL]: Sizes.FONTS['3XS'],
    [SIZES.MEDIUM]: Sizes.FONTS['2XS'],
    [SIZES.LARGE]: Sizes.FONTS['XS'],
    [SIZES.HUGE]: Sizes.FONTS['S'],
};


const Container = styled.div`
    text-align: ${props => props.alignment};

    > .title-wrapper {
        margin: ${props => props.titleMargin} 0;

        > .title {
            font-size: ${props => props.titleFontSize};
            line-height: 1.3;

            /* presence of rel attribute indicates being rendered as
               an <a> due to having an href and/or onclick prop */
            &[rel] {
                text-decoration: none;

                &:hover {
                    text-decoration: underline;
                }

                &:active {
                    text-decoration: none;
                }
            }
        }
    }

    > .stars-and-review-text {
        row-gap: 0.2rem;

        .review-text {
            font-size: ${props => props.reviewTextSize};
            line-height: 1;
        }
    }
`;

const ReviewCount = styled.span`
    font-weight: ${props => props.reviewFontWeight};
    color: ${Colours.BLACKBERRY};

    ${props => props.size === Sizes.MEDIUM && `
        font-size: ${Sizes.FONTS['2XS']};
        transform-origin: left top;
        transform: scale(0.833, 0.833);
    `}

    ${props => props.as && `
        text-decoration: none;
        border-bottom: .0625rem solid; /* computes to 1px @ root=16px */

        &:visited {
            color: ${Colours.STONE_DARKEST};
        }

        &:hover {
            color: ${Colours.BLUEBERRY_DARK};
            text-decoration: none;
            border-bottom-width: ${Sizes.SPACING.EIGHTH};
        }

        &:active {
            color: ${desaturate(0.6, Colours.BLUEBERRY_DARK)};
        }
    `}
`;



export default observer(StarReviews);
export { SIZES, ALIGNMENTS };
